import {
  getProductsQueryOptions,
  GetProductsResponse,
} from '@features/accounts/api/get-products';
import { AccountState } from '@features/accounts/enums';
import { Account } from '@features/accounts/types';
import { AccountDTO } from '@features/accounts/types/account-dto';
import {
  getBanksQueryOptions,
  GetBanksResponse,
} from '@features/performance/api/get-banks';
import { api } from '@src/lib/api-client';
import { queryClient, QueryConfig } from '@src/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { ToastUtility } from '@utils';

export type GetAccountsResponse = {
  visible: Account[];
  all: Account[];
};

export const createGetAccountsResponse = ({
  accounts,
  getBanksResponse,
  getProductsResponse,
}: {
  accounts: AccountDTO[];
  getBanksResponse: GetBanksResponse;
  getProductsResponse: GetProductsResponse;
}) => {
  const all = accounts.map((data: AccountDTO) => {
    const { checking, savings } = getProductsResponse;

    const foundProduct = [...checking, ...savings].find(
      (product) =>
        product.uuid.toLowerCase() === data.productUuid.toLowerCase(),
    );

    const foundBank = [
      ...getBanksResponse.savings,
      ...getBanksResponse.checking,
    ].find(
      (bank) =>
        bank.uuid.toLowerCase() === foundProduct?.bankUuid.toLowerCase(),
    );
    return {
      ...data,
      name: foundProduct?.name ?? '',
      fdicNumber: foundBank?.fdicNumber ?? '',
      numBasisPoints: foundProduct?.numBasisPoints ?? 0,
      numTiebreakerPoints: foundProduct?.numTiebreakerPoints ?? 0,
      preferredRate: foundProduct?.preferredRate ?? false,
      poweredBy: foundProduct?.poweredBy ?? '',
      bankMaximumBalanceCents: foundProduct?.maximumBalanceCents ?? null,
    };
  });

  const visible = all.filter((account) =>
    [
      AccountState.LINKED,
      AccountState.VERIFYING_LINK,
      AccountState.LOCKED,
    ].includes(account.state),
  );

  return { all, visible };
};

export async function getAccounts(): Promise<GetAccountsResponse> {
  const accounts = await api.get<AccountDTO[]>(`/api/v3/user/accounts`);

  const products: GetProductsResponse = await queryClient.ensureQueryData({
    queryKey: getProductsQueryOptions.queryKey,
    queryFn: getProductsQueryOptions.queryFn,
  });

  const banks: GetBanksResponse = await queryClient.ensureQueryData({
    queryKey: getBanksQueryOptions.queryKey,
    queryFn: getBanksQueryOptions.queryFn,
  });

  return createGetAccountsResponse({
    accounts: accounts.data,
    getBanksResponse: banks,
    getProductsResponse: products,
  });
}

export const getAccountsQueryOptions = queryOptions({
  queryKey: ['accounts', 'list'],
  queryFn: getAccounts,
  throwOnError: false,
});

type UseGetAccountsOptions = {
  queryConfig?: QueryConfig<typeof getAccountsQueryOptions>;
};

export const useGetAccounts = ({ queryConfig }: UseGetAccountsOptions = {}) => {
  return useQuery({ ...getAccountsQueryOptions, ...queryConfig });
};

export const callGetAccountsErrorToast = () => {
  ToastUtility.error('Failed to fetch Accounts', { toastId: 'accounts' });
};
